import RefreshIcon from '@/common/assets/icons/refreshIcon.svg?react';
import Button from '@/common/components/base/button/Button';
import LoadingButton from '@/common/components/base/button/loadingButton';
import DashedTitle from '@/common/components/base/dashedTitle/DashedTitle';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import Typography from '@/common/components/base/typography/Typography';
import ImageCard from '@/common/components/imageGallery/components/ImageCard';
import FixToBottom from '@/common/components/layouts/FixToBottom';
import UploadProgressBarModal from '@/common/components/uploadProgressBar/UploadProgressBarModal';
import useModal from '@/common/hooks/useModal';
import getVehicleTypeQueryOption from '@/common/queries/getVehicleType/getVehicleTypeQueryOPtion';
import useToast from '@/common/store/alert/useToast';
import DamagePartEmptySpace from '@/modules/adjusterReportModules/IncidentParticipants/components/images/property/damaged/vehicleImages/vehicleDamageImagePreview/DamagePartEmptySpace';
import ImageGrid from '@/modules/adjusterReportModules/IncidentParticipants/components/images/property/damaged/vehicleImages/vehicleDamageImagePreview/ImageGrid';
import { AddDamagedVehicleAdjusterQueryParamsType } from '@/modules/adjusterReportModules/IncidentParticipants/queries/addDamagedVehicleAdjuster/addDamagedVehicleAdjusterQuery.type';
import { useAddDamagedVehicleAdjusterQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/addDamagedVehicleAdjuster/addDamagedVehicleAdjusterQueryOptions';
import { createImagesFlowPostData } from '@/modules/adjusterReportModules/IncidentParticipants/utils/createImagesFlowPostData';
import useGetOfflineImages from '@/modules/OfflineFiles/hooks/useGetOfflineImages';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box, Flex } from 'styled-system/jsx';
import { grid } from 'styled-system/patterns';
import { token } from 'styled-system/tokens';

const SendOfflinePhotos = ({
  damagedId,
  retakeImage,
}: {
  damagedId: string | undefined;
  retakeImage?: () => void;
}) => {
  const {
    getCurrentFileOfflineImages,
    FileImagesList: imagesList,
    video,
    offlineError,
    DBitem,
    deleteOfflineFileData,
  } = useGetOfflineImages();

  const [progress, setProgress] = useState<number>(0);
  const controllerRef = useRef<AbortController>(new AbortController());
  const { id } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useToast();

  const {
    closeModal: uploadProgressCloseModal,
    open: uploadProgressOpen,
    openModal: uploadProgressOpenModal,
  } = useModal();
  const { data: fileVehicleType } = useQuery(
    getVehicleTypeQueryOption({ damaged_pk: damagedId, file_pk: id! }),
  );
  const { mutate } = useAddDamagedVehicleAdjusterQueryOptions();

  const handleSubmit = () => {
    if (!imagesList || !video || !DBitem) {
      return;
    }
    uploadProgressOpenModal();
    const data = createImagesFlowPostData().createPostData({
      images: imagesList,
      video: video,
    });
    const requestData: AddDamagedVehicleAdjusterQueryParamsType = {
      initial_images: data.initialImages,
      damaged_images: data.damagedImages,
      damaged_id: DBitem.damagedId!,
      id: id!,
      vehicleType: fileVehicleType?.name!,
      participantRoleType: DBitem?.userType,
      onProgress: (progress: number) => {
        setProgress(Number(progress.toFixed(2)));
      },
      signal: controllerRef.current.signal,
    };
    controllerRef.current.signal.onabort = () => {
      controllerRef.current = new AbortController();
      setProgress(0);
    };

    mutate(requestData, {
      onSuccess: onSuccessPostImages,
      onError: () => {
        setProgress(0);
      },
    });
  };

  const onSuccessPostImages = () => {
    uploadProgressCloseModal();
    setProgress(0);
    deleteOfflineFileData(DBitem?.id!);
    showAlert({
      description: 'تصاویر افلاین با موفقیت ارسال شد.',
      type: 'success',
    });
    navigate(-1);
  };

  useEffect(() => {
    getCurrentFileOfflineImages({ damagedId });
  }, []);
  if (offlineError) {
    return <Typography>{offlineError}</Typography>;
  }
  return (
    <>
      <Box>
        <Flex alignItems="center" justifyContent="space-between">
          <TitleDescription>تصاویر آفلاین خودرو</TitleDescription>
          {!!retakeImage ? (
            <Button variant="text" p={0} onClick={retakeImage}>
              <Flex gap={5} alignItems="center">
                <Typography color="purple-2" textStyle="b4">
                  عکس برداری مجدد
                </Typography>
                <RefreshIcon
                  color={token('colors.purple-2')}
                  width={14}
                  height={14}
                />
              </Flex>
            </Button>
          ) : null}
        </Flex>
        <Box mt={24}>
          <DashedTitle title="تصاویر دور تا دور خودرو" />
        </Box>
        <Flex
          gap={16}
          mt={16}
          flexDir="row"
          flexWrap="wrap"
          className={grid({ columns: 2 })}
        >
          {imagesList?.vehicleImages?.length ? (
            imagesList?.vehicleImages?.map((image, index) => (
              <Box
                key={image.url || index}
                minW={146}
                flex={
                  index === imagesList?.vehicleImages?.length - 1 &&
                  (!video?.url || imagesList?.vehicleImages?.length % 2 === 0)
                    ? 'unset'
                    : 1
                }
              >
                <ImageCard
                  imageData={{
                    name: image?.label || 'تصویر دور تا دور',
                    src: image.url,
                  }}
                />
              </Box>
            ))
          ) : (
            <Box p={4}>
              <Typography>هیچ تصویری از خودرو موجود نیست</Typography>
            </Box>
          )}
        </Flex>
        {imagesList?.damagedImages?.length ? (
          <ImageGrid
            images={imagesList?.damagedImages}
            title={<DashedTitle title="تصاویر نقاط آسیب‌دیده" />}
          />
        ) : (
          <Box mt={24}>
            <DashedTitle title="تصاویر نقاط آسیب‌دیده" />
            <Box mt={16} mb={16}>
              <DamagePartEmptySpace />
            </Box>
          </Box>
        )}

        {imagesList?.extraImages?.length ? (
          <ImageGrid
            images={imagesList?.extraImages.map((img, idx) => ({
              ...img,
              label: `تصویر تکمیلی - ${idx + 1}`,
            }))}
            title={<DashedTitle title="تصاویر تکمیلی" />}
          />
        ) : null}
        <Box mb="5.5rem" />
        <FixToBottom
          className={css({ bottom: '0px', backgroundColor: 'white' })}
        >
          <LoadingButton
            loading={false}
            onClick={handleSubmit}
            fullWidth
            variant="contained"
            mt={16}
          >
            ارسال تصاویر
          </LoadingButton>
        </FixToBottom>
      </Box>
      {uploadProgressOpen && (
        <UploadProgressBarModal
          closeModal={uploadProgressCloseModal}
          open={uploadProgressOpen}
          progress={progress}
        />
      )}
    </>
  );
};

export default SendOfflinePhotos;
